import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import moment from 'moment';
import { Formik, Form } from 'formik';

import Group from '@lightspeed/cirrus-group';
import Text from '@lightspeed/cirrus-text';
import { Divider } from '@lightspeed/flame/Divider';
import Input from '@lightspeed/cirrus-input';
import { Button } from '@lightspeed/flame/Button';
import { Alert } from '@lightspeed/flame/Alert';
import { IconArrowRight } from '@lightspeed/flame/Icon/ArrowRight';
import { Radio } from '@lightspeed/flame/Radio';

import { MIDNIGHT } from 'pages/utils/time';
import { END_TIME, START_TIME } from './index';
import { validate } from './utils';

const TimeRangePickerForm = ({
  startTime,
  endTime,
  allDay,
  timeFormat,
  focusedInput,
  onStartTimeFocus,
  onEndTimeFocus,
  onSubmit,
}) => (
  <Formik
    autocomplete="off"
    onSubmit={onSubmit}
    initialValues={{
      [`${START_TIME}`]: startTime,
      [`${END_TIME}`]: endTime,
      allDay,
    }}
    validateOnChange={false}
    validate={validate(timeFormat)}
    render={({ values, errors, setFieldValue, setFieldError, handleChange, handleBlur }) => {
      const startTimeCls = cn({ 'input-focus': !values.allDay && focusedInput === START_TIME });
      const endTimeCls = cn({
        'input-focus': !values.allDay && focusedInput === END_TIME,
      });

      const formatTimeField = field => {
        const formattedValue = moment(values[field], timeFormat);

        if (formattedValue.isValid()) {
          setFieldValue(field, formattedValue.format(timeFormat));
        }
      };

      const handleStartTimeBlur = event => {
        handleBlur(event);
        formatTimeField(START_TIME);
      };

      const handleEndTimeBlur = event => {
        handleBlur(event);
        formatTimeField(END_TIME);
      };

      const handleAllDayChange = event => {
        const { target: { value } = {} } = event;

        handleChange(event);
        if (value === 'true') {
          setFieldValue(START_TIME, moment(MIDNIGHT).format(timeFormat));
          setFieldValue(END_TIME, moment(MIDNIGHT).format(timeFormat));
          setFieldError(START_TIME, '');
          setFieldError(END_TIME, '');
        }
      };

      return (
        <Form autoComplete="off">
          <div className="cr-time-picker__form">
            <div className="cr-p-2">
              <Group block>
                <div className="cr-time-picker__radio">
                  <Radio
                    label="All day"
                    name="allDay"
                    checked={values.allDay === 'true'}
                    value="true"
                    onChange={handleAllDayChange}
                    data-test="time-picker-all-day-radio"
                  />
                </div>
                <div className="cr-time-picker__radio cr-flex-1">
                  <Radio
                    label="Custom"
                    name="allDay"
                    checked={!(values.allDay === 'true')}
                    value="false"
                    onChange={handleAllDayChange}
                    data-test="time-picker-custom-time-radio"
                  />
                </div>
                <div className="cr-time-picker__apply cr-text-right">
                  <Button type="submit" variant="primary" fill data-test="time-picker-submit-input">
                    Apply
                  </Button>
                </div>
              </Group>
            </div>
            {(errors[START_TIME] || errors[END_TIME]) && (
              <Alert type="danger" noCloseBtn>
                <Fragment>
                  {errors[START_TIME] && errors[START_TIME] !== errors[END_TIME] && (
                    <Text>{errors[START_TIME]}</Text>
                  )}
                  {errors[END_TIME] && <Text>{errors[END_TIME]}</Text>}
                </Fragment>
              </Alert>
            )}
            <Divider />
            <div className="cr-p-2">
              <Group>
                <div className={startTimeCls}>
                  <Input
                    style={{ width: '106px' }}
                    name={START_TIME}
                    onFocus={onStartTimeFocus}
                    onBlur={handleStartTimeBlur}
                    onChange={handleChange}
                    value={values[START_TIME]}
                    autoComplete="off"
                    status={{
                      type: errors[START_TIME] ? 'error' : undefined,
                    }}
                    data-test="time-picker-start-time-input"
                    disabled={values.allDay === 'true'}
                    placeholder={timeFormat}
                  />
                </div>
                <IconArrowRight />
                <div className={endTimeCls}>
                  <Input
                    style={{ width: '106px' }}
                    name={END_TIME}
                    onFocus={onEndTimeFocus}
                    onBlur={handleEndTimeBlur}
                    onChange={handleChange}
                    value={values[END_TIME]}
                    autoComplete="off"
                    status={{
                      type: errors[END_TIME] ? 'error' : undefined,
                    }}
                    data-test="time-picker-end-time-input"
                    disabled={values.allDay === 'true'}
                    placeholder={timeFormat}
                  />
                </div>
              </Group>
            </div>
            <Divider />
          </div>
        </Form>
      );
    }}
  />
);

TimeRangePickerForm.propTypes = {
  startTime: PropTypes.string.isRequired,
  endTime: PropTypes.string.isRequired,
  allDay: PropTypes.oneOf(['true', 'false']).isRequired,
  timeFormat: PropTypes.string.isRequired,
  focusedInput: PropTypes.oneOf(['startTime', 'endTime']).isRequired,
  onStartTimeFocus: PropTypes.func,
  onEndTimeFocus: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default TimeRangePickerForm;
